import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { AtSign, Check, X, Loader2, Sparkles } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';
import { checkHandleAvailability, safeJson } from '../utils/safeJson';



export default function ClaimHandlePage() {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [handle, setHandle] = useState('');
  const [checking, setChecking] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [validationError, setValidationError] = useState('');
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // If user already has a handle, redirect to home
  useEffect(() => {
    if (user?.has_handle || user?.handle) {
      navigate('/home');
    }
  }, [user, navigate]);

  // Local validation
  const validateHandle = (value) => {
    const clean = value.toLowerCase().replace('@', '');
    
    if (!clean) {
      return { valid: false, error: '' };
    }
    if (clean.length < 3) {
      return { valid: false, error: 'At least 3 characters required' };
    }
    if (clean.length > 20) {
      return { valid: false, error: 'Maximum 20 characters' };
    }
    if (!/^[a-z]/.test(clean)) {
      return { valid: false, error: 'Must start with a letter' };
    }
    if (!/^[a-z][a-z0-9_]*$/.test(clean)) {
      return { valid: false, error: 'Only letters, numbers, and underscores' };
    }
    return { valid: true, error: '' };
  };

  // Debounced API check with abort support
  const checkAvailabilityDebounced = useCallback((value) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    const clean = value.toLowerCase().replace('@', '');
    if (clean.length < 3) {
      setAvailability(null);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController();
      setChecking(true);
      
      try {
        // API is aliased from API_BASE_URL, need to add /api for the full endpoint path
        const result = await checkHandleAvailability(
          clean,
          `${API}/api`,
          abortControllerRef.current.signal
        );
        
        if (result.error !== 'aborted') {
          setAvailability(result);
        }
      } catch (error) {
        console.error('Error checking handle:', error);
        setAvailability({
          available: false,
          reason: "Can't verify handle right now",
          handle: clean
        });
      } finally {
        setChecking(false);
      }
    }, 400);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    let value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setHandle(value);
    
    const validation = validateHandle(value);
    setValidationError(validation.error);
    
    if (validation.valid) {
      setAvailability(null);
      checkAvailabilityDebounced(value);
    } else {
      setAvailability(null);
    }
  };

  const handleClaim = async () => {
    if (!handle || !availability?.available) return;

    setClaiming(true);
    try {
      const res = await fetch(`${API}/api/handle/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ handle })
      });

      const result = await safeJson(res);

      if (result.ok) {
        toast.success(`Welcome, @${handle}!`);
        if (refreshUser) await refreshUser();
        navigate('/home');
      } else {
        toast.error(result.error || 'Failed to claim handle');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const isValid = handle.length >= 3 && !validationError && availability?.available;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <AtSign className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Choose Your Handle</h1>
          <p className="text-muted-foreground">
            Pick a unique @handle for your DMV Soundstage profile
          </p>
        </div>

        <div className="space-y-6">
          {/* Handle Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Your Handle</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">
                @
              </span>
              <Input
                value={handle}
                onChange={handleInputChange}
                placeholder="yourhandle"
                className="pl-8 text-lg h-12"
                maxLength={20}
                autoFocus
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checking && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
                {!checking && availability?.available && (
                  <Check className="w-5 h-5 text-green-500" />
                )}
                {!checking && availability && !availability.available && (
                  <X className="w-5 h-5 text-red-500" />
                )}
              </div>
            </div>
            
            {/* Validation/Availability Message */}
            <div className="h-5">
              {validationError && (
                <p className="text-sm text-red-500">{validationError}</p>
              )}
              {!validationError && availability && (
                <p className={`text-sm ${availability.available ? 'text-green-500' : 'text-red-500'}`}>
                  {availability.reason}
                </p>
              )}
            </div>
          </div>

          {/* Handle Preview */}
          {handle && !validationError && (
            <div className="bg-muted/50 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground mb-1">Your profile will appear as:</p>
              <p className="text-xl font-bold text-primary">@{handle}</p>
            </div>
          )}

          {/* Rules */}
          <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
            <p className="font-medium flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Handle Rules
            </p>
            <ul className="text-muted-foreground space-y-1 ml-6">
              <li>• 3-20 characters</li>
              <li>• Letters, numbers, and underscores only</li>
              <li>• Must start with a letter</li>
              <li>• You can change it once every 30 days</li>
            </ul>
          </div>

          {/* Claim Button */}
          <Button
            onClick={handleClaim}
            disabled={!isValid || claiming}
            className="w-full h-12 text-lg"
            size="lg"
          >
            {claiming ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Claiming...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Claim @{handle || 'handle'}
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
}
